"use client";

import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  checkAnonSessionExpiry,
  subscribeAnonSessionExpiry,
  touchAnonSession,
} from "@/lib/auth-storage";

/**
 * Enforces the anonymous session timeout (requirement 1b).
 *
 * A signed-out visitor's data lives only in heap, so it never survives a
 * reload. What that alone does not cover is the tab left open all afternoon:
 * the entries stay on screen and in React state indefinitely. This component
 * closes that gap — after an hour with no interaction it reloads the page,
 * which discards both the memory store and every bit of component state, and
 * then explains what happened.
 *
 * Signed-in users are unaffected; their session is governed by the Firebase
 * login window instead (see AuthContext).
 */

/** How often to test for idleness. Fine-grained enough for a one-hour window. */
const CHECK_INTERVAL_MS = 60 * 1000;

/**
 * Marks the reload as a timeout rather than a normal navigation. A query
 * parameter, not storage: an anonymous session must not leave anything behind,
 * not even a "you were logged out" flag.
 */
const EXPIRED_PARAM = "session_expired";

/** Activity that should keep a session alive, even with no data changing. */
const ACTIVITY_EVENTS = [
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
] as const;

function reloadAsExpired() {
  const url = new URL(window.location.href);
  url.searchParams.set(EXPIRED_PARAM, "1");
  // replace(), so Back does not return to a page still showing stale entries.
  window.location.replace(url.toString());
}

export function AnonSessionTimeout() {
  const { status } = useAuth();
  const { translate } = useLanguage();
  const [showNotice, setShowNotice] = useState(false);

  // Did we arrive here from a timeout? Read once on mount, then strip the
  // parameter so a refresh or a shared link does not repeat the message.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(EXPIRED_PARAM)) return;
    setShowNotice(true);
    url.searchParams.delete(EXPIRED_PARAM);
    window.history.replaceState(null, "", url.toString());
  }, []);

  useEffect(() => {
    // "loading" is not yet a decision: a returning signed-in user passes
    // through it, and expiring them mid-restore would be wrong.
    if (status !== "unauthenticated") return;

    // Start the clock even if no context has touched storage yet.
    touchAnonSession();

    const handleActivity = () => touchAnonSession();
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, handleActivity, { passive: true })
    );

    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      // A machine that slept through the window: check on the way back in
      // rather than waiting up to another minute for the interval.
      if (!checkAnonSessionExpiry()) handleActivity();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    const unsubscribe = subscribeAnonSessionExpiry(reloadAsExpired);
    const interval = window.setInterval(checkAnonSessionExpiry, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, handleActivity)
      );
      document.removeEventListener("visibilitychange", handleVisibility);
      window.clearInterval(interval);
      unsubscribe();
    };
  }, [status]);

  const dismiss = useCallback(() => setShowNotice(false), []);

  if (!showNotice) return null;

  return (
    <Dialog open onOpenChange={(open) => !open && dismiss()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{translate("sessionExpiredTitle")}</DialogTitle>
          <DialogDescription>
            {translate("sessionExpiredBody")}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={dismiss}>{translate("sessionExpiredAction")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
